import {
  CameraAlt as CameraIcon,
  Error as ErrorIcon,
  QrCodeScanner as QrCodeScannerIcon,
} from '@mui/icons-material';
import { Button, CircularProgress, Typography } from '@mui/material';
import QrScanner from 'qr-scanner';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isStr } from '@jebel/utils';

import { recordError } from 'shared/utils/record';

import {
  Container,
  FeedbackContainer,
  IconWrapper,
  StatusBar,
  Video,
  VideoContainer,
} from './QRCodeReader.styles';

export enum QRCodeReaderStatus {
  IDLE = 'idle',
  REQUESTING_PERMISSION = 'requesting-permission',
  SCANNING = 'scanning',
  DETECTED = 'detected',
  ERROR = 'error',
}

export interface QRCodeReaderResult {
  /** The raw value decoded from the QR code. */
  value: string;
  /** The corner points of the detected QR code. */
  cornerPoints: QrScanner.Point[];
}

export interface QRCodeReaderProps {
  /**
   * Callback fired when a QR code is successfully detected.
   * @param result - The decoded QR code data.
   */
  onDetect?(result: QRCodeReaderResult): void;

  /**
   * Callback fired when an error occurs.
   * @param error - The error that occurred.
   */
  onError?(error: unknown): void;

  /**
   * Whether to automatically start scanning when the component mounts.
   * @default true
   */
  autoStart?: boolean;

  /**
   * Whether to stop scanning after the first detection.
   * @default true
   */
  stopOnDetect?: boolean;

  /**
   * Custom class name for the container.
   */
  className?: string;

  /**
   * Width of the scanner component.
   * @default '100%'
   */
  width?: string | number;

  /**
   * Preferred camera to use.
   * @default 'environment'
   */
  preferredCamera?: 'environment' | 'user';
}

/**
 * A reusable QR code reader component using the qr-scanner library.
 *
 * @see https://www.npmjs.com/package/qr-scanner
 *
 * @example
 * ```tsx
 * <QRCodeReader
 *   onDetect={(result) => console.log('Detected:', result.value)}
 *   onError={(error) => console.error('Error:', error)}
 * />
 * ```
 */
export function QRCodeReader(props: QRCodeReaderProps) {
  const {
    onDetect,
    onError,
    autoStart = false,
    stopOnDetect = true,
    className,
    width = '100%',
    preferredCamera = 'environment',
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);

  const [status, setStatus] = useState<QRCodeReaderStatus>(QRCodeReaderStatus.IDLE);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const stopScanning = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop();
      scannerRef.current.destroy();
      scannerRef.current = null;
    }

    setStatus(QRCodeReaderStatus.IDLE);
  }, []);

  const setError = useCallback(
    (error: unknown) => {
      setStatus(QRCodeReaderStatus.ERROR);
      stopScanning();
      onError?.(error);

      if (error instanceof Error) {
        setErrorMessage(error.message);
      }

      if (isStr(error)) {
        setErrorMessage(error);
      }
    },
    [onError, stopScanning],
  );

  const startScanning = useCallback(async () => {
    const hasCamera = await QrScanner.hasCamera();

    if (!hasCamera) {
      setError('No camera was found on this device.');
      return;
    }

    setStatus(QRCodeReaderStatus.REQUESTING_PERMISSION);
    setErrorMessage(null);

    if (!videoRef.current) {
      return;
    }

    try {
      const scanner = new QrScanner(
        videoRef.current,
        scanResult => {
          const result: QRCodeReaderResult = {
            value: scanResult.data,
            cornerPoints: scanResult.cornerPoints,
          };

          setStatus(QRCodeReaderStatus.DETECTED);
          onDetect?.(result);

          if (stopOnDetect) {
            stopScanning();
          }
        },
        {
          preferredCamera,
          highlightScanRegion: false,
          highlightCodeOutline: false,
        },
      );

      scannerRef.current = scanner;

      await scanner.start();
      setStatus(QRCodeReaderStatus.SCANNING);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));

      recordError(error);

      if (error.message.includes('permission') || error.message.includes('denied')) {
        setError('Camera access was denied. Please allow camera access to scan QR codes.');
      }

      if (error.message.includes('not found') || error.message.includes('no camera')) {
        setError('No camera was found on this device.');
      }

      if (error.message.includes('in use') || error.message.includes('busy')) {
        setError('Camera is already in use by another application.');
      }

      setStatus(QRCodeReaderStatus.ERROR);
    }
  }, [videoRef, preferredCamera, stopOnDetect, onDetect, stopScanning, setError]);

  const handleRetry = useCallback(() => {
    setStatus(QRCodeReaderStatus.IDLE);
    setErrorMessage(null);
    startScanning();
  }, [startScanning]);

  useEffect(() => {
    if (autoStart) {
      startScanning();
    }

    return () => {
      stopScanning();
    };
  }, [autoStart, startScanning, stopScanning]);

  if (status === QRCodeReaderStatus.ERROR) {
    return (
      <Container className={className} style={{ width }}>
        <FeedbackContainer>
          <IconWrapper variant="error">
            <ErrorIcon />
          </IconWrapper>

          <Typography variant="h6" style={{ color: '#fff' }}>
            Camera Error
          </Typography>

          <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {errorMessage}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            startIcon={<CameraIcon />}
          >
            Try Again
          </Button>
        </FeedbackContainer>
      </Container>
    );
  }

  if (status === QRCodeReaderStatus.IDLE) {
    return (
      <Container className={className} style={{ width }}>
        <FeedbackContainer>
          <IconWrapper>
            <CameraIcon />
          </IconWrapper>

          <Typography variant="h6" style={{ color: '#fff' }}>
            Camera Ready
          </Typography>

          <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Click below to start scanning
          </Typography>

          {status === QRCodeReaderStatus.IDLE && (
            <Button
              variant="contained"
              color="primary"
              onClick={startScanning}
              startIcon={<QrCodeScannerIcon />}
            >
              Start Scanning
            </Button>
          )}
        </FeedbackContainer>
      </Container>
    );
  }

  return (
    <Container className={className} style={{ width }}>
      <VideoContainer>
        <Video ref={videoRef} playsInline muted />
      </VideoContainer>

      <StatusBar>
        {status === QRCodeReaderStatus.REQUESTING_PERMISSION && (
          <>
            <CircularProgress size={16} color="inherit" />
            <Typography variant="body2">Requesting camera access...</Typography>
          </>
        )}

        {status === QRCodeReaderStatus.SCANNING && (
          <>
            <CircularProgress size={16} color="inherit" />
            <Typography variant="body2">Point camera at a QR code</Typography>
          </>
        )}

        {status === QRCodeReaderStatus.DETECTED && (
          <Typography variant="body2" color="success">
            ✓ QR Code detected!
          </Typography>
        )}
      </StatusBar>
    </Container>
  );
}
